import React, { useState } from 'react'
import { Authenticator } from '@aws-amplify/ui-react'
import ShortcodeList from './ShortcodeList'
import ShortcodeDetail from './ShortcodeDetail'
import CreateShortcode from './CreateShortcode'
import { Shortcode } from '../types'

// Helper function to extract user's display name
const getUserDisplayName = (user: any): string => {
  // Try to get the actual name from user attributes
  const attributes = user?.attributes || {}
  
  // Prefer given_name + family_name combination
  if (attributes.given_name && attributes.family_name) {
    return `${attributes.given_name} ${attributes.family_name}`
  }
  
  // Fallback to name attribute
  if (attributes.name) {
    return attributes.name
  }
  
  // Fallback to email (without domain)
  if (attributes.email) {
    return attributes.email.split('@')[0]
  }
  
  // Final fallback to username (Google ID)
  return user?.username || 'User'
}

const Dashboard: React.FC = () => {
  const [selectedShortcode, setSelectedShortcode] = useState<Shortcode | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [shortcodes, setShortcodes] = useState<Shortcode[]>([])

  const handleShortcodeSelect = (shortcode: Shortcode) => {
    setSelectedShortcode(shortcode)
    setShowCreateForm(false)
  }

  const handleCreateNew = () => {
    setShowCreateForm(true)
    setSelectedShortcode(null)
  }

  const handleShortcodeCreated = (newShortcode: Shortcode) => {
    setShortcodes(prev => [newShortcode, ...prev])
    setShowCreateForm(false)
    setSelectedShortcode(newShortcode)
  }

  const handleShortcodeUpdated = (updatedShortcode: Shortcode) => {
    setShortcodes(prev => 
      prev.map(sc => sc.shortcode === updatedShortcode.shortcode ? updatedShortcode : sc)
    )
    setSelectedShortcode(updatedShortcode)
  }

  const handleShortcodeDeleted = (shortcode: string) => {
    setShortcodes(prev => prev.filter(sc => sc.shortcode !== shortcode))
    setSelectedShortcode(null)
  }

  return (
    <Authenticator hideSignUp={true} socialProviders={['google']}>
      {({ user }) => {
        const displayName = getUserDisplayName(user)
        
        return (
          <div className="h-screen flex flex-col bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
            {/* Fixed Header */}
            <header className="bg-white/90 backdrop-blur-sm border-b border-white/20 shadow-sm flex-shrink-0">
              <div className="max-w-7xl mx-auto px-6 py-6">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <h1 className="text-display text-gray-900">OfferSpotter URL Manager</h1>
                    <p className="text-caption text-gray-600">Welcome, {displayName}</p>
                  </div>
                  <button
                    onClick={handleCreateNew}
                    className="btn-primary hover-lift"
                  >
                    Create New Shortcode
                  </button>
                </div>
              </div>
            </header>

            {/* Scrollable Main Content */}
            <div className="flex-1 overflow-hidden">
              <div className="max-w-7xl mx-auto mobile-padding py-8 h-full">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mobile-space-y h-full min-h-0">
                  {/* Left Panel - Shortcode List (Scrollable) */}
                  <div className="lg:col-span-1 h-full flex flex-col min-h-0">
                    <div className="flex-1 overflow-hidden min-h-0">
                      <div className="h-full overflow-y-auto custom-scrollbar pb-4">
                        <ShortcodeList 
                          shortcodes={shortcodes}
                          onShortcodeSelect={handleShortcodeSelect}
                          selectedShortcode={selectedShortcode}
                          onShortcodesUpdate={setShortcodes}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Detail/Create Form (Scrollable) */}
                  <div className="lg:col-span-2 h-full flex flex-col min-h-0">
                    <div className="flex-1 overflow-hidden min-h-0">
                      <div className="h-full overflow-y-auto custom-scrollbar pb-4">
                        {showCreateForm ? (
                          <div className="fade-in">
                            <CreateShortcode 
                              onSuccess={handleShortcodeCreated}
                              onCancel={() => setShowCreateForm(false)}
                            />
                          </div>
                        ) : selectedShortcode ? (
                          <div className="fade-in">
                            <ShortcodeDetail 
                              shortcode={selectedShortcode}
                              onUpdate={handleShortcodeUpdated}
                              onDelete={handleShortcodeDeleted}
                            />
                          </div>
                        ) : (
                          <div className="card space-inside-lg text-center fade-in h-full flex items-center justify-center">
                            <div>
                              <div className="text-6xl mb-4 text-gray-300">🔗</div>
                              <h3 className="text-heading text-gray-700 mb-2">Get Started</h3>
                              <p className="text-body text-gray-600">Select a shortcode from the list or create a new one to begin</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }}
    </Authenticator>
  )
}

export default Dashboard